import React, { useState, useContext, useEffect } from 'react';
import { Card, Button, Modal } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { PRODUCT_ROUTE } from "../utils/consts";
import { Context } from '../index';
import { FaStar, FaShoppingCart } from 'react-icons/fa';
import '../styles/ProductItem.css';
import { useToast } from '../contexts/ToastContext';
import { createPortal } from 'react-dom';
import { observer } from 'mobx-react-lite';
import { fetchOneProduct } from '../http/productAPI';
import { getImageUrl, buildPictureSources } from '../utils/imageUtils';

function forceModalCleanup() {
  document.body.classList.remove('modal-open');
  document.body.style.overflow = '';
  document.body.style.paddingRight = '';
  document.body.removeAttribute('data-rr-ui-modal-open');
  // Удаляем пустые модальные div
  document.querySelectorAll('.modal').forEach(modal => {
    if (!modal.innerHTML.trim()) modal.remove();
  });
}

const ProductItem = observer(({ product }) => {
    const location = useLocation();
    const { user } = useContext(Context);
    const { showToast } = useToast();
    const [isHovered, setIsHovered] = useState(false);
    const [selectedVariantIdx, setSelectedVariantIdx] = useState(-1);
    const [showModal, setShowModal] = useState(false);
    const [productDetails, setProductDetails] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [isFavorite, setIsFavorite] = useState(false);
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
        return () => {
            setIsMounted(false);
            forceModalCleanup();
        };
    }, []);

    // Определяем количество товара на складе
    // ВАЖНО: для товаров с вариантами приоритет у variants.quantity.
    // product_stores может быть агрегирован/задвоен и давать завышенные цифры.
    let stock = 0;
    if ((product.type === 'shoe' || product.type === 'clothes' || product.type === 'toy') && Array.isArray(product.variants) && product.variants.length > 0) {
        stock = product.variants.reduce((sum, v) => sum + (Number(v.quantity) || 0), 0);
    } else if (Array.isArray(product.product_stores) && product.product_stores.length > 0) {
        stock = product.product_stores.reduce((sum, ps) => sum + (Number(ps.quantity) || 0), 0);
    } else if (product.type === 'toy') {
        // Для игрушек без вариантов проверяем поля quantity и toy?.quantity
        if (typeof product.quantity === 'number') {
            stock = product.quantity;
        } else if (product.toy && typeof product.toy.quantity === 'number') {
            stock = product.toy.quantity;
        } else if (typeof product.stock === 'number') {
            stock = product.stock;
        } else {
            stock = 0;
        }
    } else {
        stock = product.stock || 0;
    }

    // Получаем одну фотографию
    const image = product.image_url;

    // Вычисление рейтинга
    let ratingValue = 0;
    if (product.averageRating !== undefined) {
        ratingValue = Number(product.averageRating);
    } else if (product.rating !== undefined) {
        ratingValue = Number(product.rating);
    } else if (Array.isArray(product.ratings) && product.ratings.length > 0) {
        ratingValue = product.ratings.reduce((sum, r) => sum + (r.score || 0), 0) / product.ratings.length;
    }

    // Вычисление скидки (если есть)
    const discount = product.discount || 0;
    const discountedPrice = discount > 0 ? product.price * (1 - discount / 100) : product.price;

    const handleShowModal = async () => {
        try {
            const data = await fetchOneProduct(product.id);
            setProductDetails(data);
            setSelectedVariantIdx(-1); // Сбрасываем выбор варианта при открытии модального окна
            setShowModal(true);
        } catch (error) {
            console.error('Error fetching product details:', error);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedVariantIdx(-1);
        setQuantity(1);
        forceModalCleanup();
    };

    const getVariantStock = (v) => {
        if (!v) return 0;
        if (v.quantity !== undefined && v.quantity !== null) return Number(v.quantity) || 0;
        if (Array.isArray(v.product_stores) && v.product_stores.length > 0) {
            return v.product_stores.reduce((s, ps) => s + (Number(ps.quantity) || 0), 0);
        }
        return 0;
    };
    const selectedVariant = productDetails?.variants?.[selectedVariantIdx] ?? product?.variants?.[selectedVariantIdx];
    const modalMaxStock = selectedVariant ? getVariantStock(selectedVariant) : stock;

    useEffect(() => {
        if (showModal && quantity > modalMaxStock && modalMaxStock > 0) {
            setQuantity(Math.min(quantity, modalMaxStock));
        }
    }, [showModal, selectedVariantIdx, modalMaxStock, quantity]);

    const handleVariantSelect = (variant, idx) => {
        // Используем переданный индекс напрямую, так как он соответствует индексу в массиве productDetails.variants
        console.log('[ProductItem] Выбран вариант:', variant, 'индекс:', idx);
        setSelectedVariantIdx(idx);
    };

    const handleQuantityChange = (e) => {
        const value = parseInt(e.target.value, 10);
        if (!isNaN(value) && value > 0) {
            setQuantity(Math.min(value, Math.max(1, modalMaxStock)));
        }
    };

    const handleVariantAdd = async () => {
        const needsVariantSelection = (product.type === 'shoe' || product.type === 'clothes' || product.type === 'toy');
        
        // Используем productDetails.variants, так как они загружены в модальном окне
        const variants = productDetails?.variants || product.variants || [];
        
        // Проверяем, что вариант выбран
        if (needsVariantSelection && variants.length > 1 && selectedVariantIdx === -1) {
            showToast('Выберите вариант товара', 'error');
            return;
        }
        
        // Получаем выбранный вариант или первый, если только один вариант
        let variant = null;
        if (variants.length > 0) {
            if (selectedVariantIdx >= 0 && variants[selectedVariantIdx]) {
                // Используем выбранный вариант
                variant = { ...variants[selectedVariantIdx] };
                console.log('[ProductItem] Используется выбранный вариант:', variant, 'индекс:', selectedVariantIdx);
            } else if (variants.length === 1) {
                // Если только один вариант, используем его
                variant = { ...variants[0] };
                console.log('[ProductItem] Используется единственный вариант:', variant);
            } else {
                showToast('Выберите вариант товара', 'error');
                return;
            }
        }

        const variantStock = getVariantStock(variant);
        if (variant && variantStock <= 0) {
            setShowModal(false);
            setSelectedVariantIdx(-1);
            setTimeout(() => showToast('Товара нет в наличии', 'error'), 200);
            return;
        }
        if (quantity > variantStock) {
            setShowModal(false);
            setSelectedVariantIdx(-1);
            setTimeout(() => showToast(`Доступно только ${variantStock} шт.`, 'error'), 200);
            return;
        }
        setShowModal(false);
        setSelectedVariantIdx(-1);

        try {
            if (variant) {
                if (!variant.storeId && Array.isArray(product.product_stores) && product.product_stores.length > 0) {
                    variant.storeId = product.product_stores[0].storeId;
                }
                if (variant.storeId && !variant.storeName && product.stores) {
                    const storeObj = product.stores.find(s => s.id === variant.storeId);
                    if (storeObj && typeof storeObj.name === 'string') {
                        variant.storeName = storeObj.name;
                    }
                }
            }
            const storeIdForBasket = variant?.storeId || product.store_id || (product.product_stores && product.product_stores[0]?.storeId) || null;
            await user.addToBasket(product, variant, quantity, storeIdForBasket, variantStock);
            setTimeout(() => showToast('Товар добавлен в корзину', 'success'), 200);
        } catch (error) {
            console.error('Error adding to cart:', error);
            setTimeout(() => showToast(error.response?.data?.message || 'Ошибка при добавлении в корзину', 'error'), 200);
        }
    };

    const handleAddToBasket = async () => {
        if (product.variants && product.variants.length > 0) {
            handleShowModal();
        } else {
            if (stock === 0) {
                showToast('Товара нет в наличии', 'error');
                return;
            }
            try {
                let store_id = product.store_id;
                let variant = null;
                if (product.type === 'toy') {
                    if (Array.isArray(product.product_stores) && product.product_stores.length > 0) {
                        store_id = product.product_stores[0].storeId;
                        if (product.stores && Array.isArray(product.stores)) {
                            const storeObj = product.stores.find(s => s.id === store_id);
                            if (storeObj && typeof storeObj.name === 'string') {
                                variant = { storeId: store_id, storeName: storeObj.name };
                            }
                        }
                    }
                }
                if (variant && typeof variant === 'object' && variant.storeId && !variant.storeName && product.stores) {
                    const storeObj = product.stores.find(s => s.id === variant.storeId);
                    if (storeObj && typeof storeObj.name === 'string') {
                        variant.storeName = storeObj.name;
                    }
                }
                await user.addToBasket(product, variant, 1, store_id, stock);
                showToast('Товар добавлен в корзину', 'success');
            } catch (e) {
                showToast(e.response?.data?.message || 'Ошибка при добавлении в корзину', 'error');
            }
        }
    };

    const getTypeLabel = (type) => {
        const types = {
            'shoe': 'Обувь',
            'toy': 'Игрушка',
            'clothes': 'Одежда'
        };
        return types[type] || type;
    };

    const handleFavorite = (e) => {
        e.stopPropagation();
        setIsFavorite(!isFavorite);
        // TODO: Добавить логику сохранения в избранное
    };

    const handleCardClick = () => {
        const searchParams = new URLSearchParams(location.search);
        const queryString = searchParams.toString();
        const path = PRODUCT_ROUTE + '/' + product.id + (queryString ? '?' + queryString : '');
        const a = document.createElement('a');
        a.href = path;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.click();
    };

    const renderModal = () => {
        if (!isMounted || !showModal) return null;
        return createPortal(
            <Modal
                show={showModal}
                onHide={handleCloseModal}
                centered
                backdrop="static"
                unmountOnExit={true}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{product.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {productDetails && (
                        <>
                            <p>{productDetails.description}</p>
                                        {productDetails.variants && productDetails.variants.length > 0 && (
                                <div className="mb-3">
                                    <h5>Выберите вариант из списка:</h5>
                                    <div className="d-flex flex-wrap gap-2">
                                        {productDetails.variants.map((variant, idx) => (
                                            <Button
                                                key={variant.id || idx}
                                                variant={selectedVariantIdx === idx ? 'primary' : 'outline-primary'}
                                                style={selectedVariantIdx === idx ? { backgroundColor: '#0d6efd', color: '#fff', border: '2px solid #0d6efd', fontWeight: 600 } : {}}
                                                onClick={() => handleVariantSelect(variant, idx)}
                                            >
                                                {variant.name || variant.size || '[нет названия]'}
                                                {variant.size ? ` / ${variant.size}` : ''}
                                                {variant.color ? ` / ${variant.color}` : ''}
                                                {variant.quantity !== undefined ? ` (${variant.quantity} шт.)` : ''}
                                                <br />
                                                <span style={{ fontSize: '0.85em', color: selectedVariantIdx === idx ? '#fff' : '#0d6efd' }}>
                                                    Магазин: {variant.storeName || 'не указан'}
                                                </span>
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="mb-3">
                                <label htmlFor="quantity" className="form-label">Количество:</label>
                                <input
                                    type="number"
                                    id="quantity"
                                    className="form-control"
                                    value={quantity}
                                    onChange={handleQuantityChange}
                                    min="1"
                                    max={Math.max(1, modalMaxStock)}
                                />
                                {modalMaxStock > 0 && modalMaxStock < 999 && (
                                    <small className="text-muted">Доступно: {modalMaxStock} шт.</small>
                                )}
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Отмена
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleVariantAdd}
                        disabled={selectedVariantIdx === -1 || stock === 0}
                    >
                        Добавить в корзину
                    </Button>
                </Modal.Footer>
            </Modal>,
            document.body
        );
    };

    console.log('[ProductItem]', product, 'imgPath:', getImageUrl(product.image_url));

    // --- Стили для line-clamp и адаптивности ---
    const titleStyle = {
        fontWeight: 600,
        fontSize: '1rem',
        margin: '8px 0',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minHeight: '2.4em', // чтобы всегда было место под 2 строки
    };
    const cardFooterStyle = {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        minHeight: 60,
    };

    return (
        <>
            <div
                role="link"
                tabIndex={0}
                className="product-card"
                style={{ display: 'flex', flexDirection: 'column', minHeight: 380, cursor: 'pointer' }}
                onClick={handleCardClick}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCardClick();
                    }
                }}
            >
                <div className="product-image-container">
                    {(() => {
                        const { avif, webp, fallback } = buildPictureSources(product.image_url, 400);
                        return (
                            <picture>
                                {avif && <source srcSet={avif} type="image/avif" />}
                                {webp && <source srcSet={webp} type="image/webp" />}
                                <img
                                    src={fallback}
                        alt={product.name}
                        loading="lazy"
                                    decoding="async"
                                    width={300}
                                    height={300}
                        className="product-image"
                                    style={{ aspectRatio: '1 / 1', objectFit: 'cover' }}
                                    onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = '/logo192.PNG'; }}
                    />
                            </picture>
                        );
                    })()}
                </div>
                <Card.Body className="product-card-body" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', minHeight: 0 }}>
                    <div className="product-type">
                        {getTypeLabel(product.type)}
                    </div>
                    <div className="product-title" style={titleStyle}>{product.name}</div>
                    <div className="product-rating" style={{ marginTop: 2 }}>
                        <FaStar className="text-warning" />
                        <span>{ratingValue > 0 ? ratingValue.toFixed(1) : 'Нет оценок'}</span>
                    </div>
                    <div className="product-price">
                        {discount > 0 ? (
                            <div className="price-with-discount enhanced-price-mobile">
                                <span className="original-price">{product.price.toLocaleString()} ₽</span>
                                <span className="discounted-price">{discountedPrice.toLocaleString()} ₽</span>
                                <span className="discount-label">Скидка {discount}%</span>
                            </div>
                        ) : (
                            <span>{product.price.toLocaleString()} ₽</span>
                        )}
                    </div>
                    <div style={{ flex: 1 }} />
                    <div className="product-card-footer">
                        <Button 
                            variant="primary" 
                            className="add-to-cart-button"
                            onClick={e => { e.stopPropagation(); handleAddToBasket(); }}
                            disabled={stock === 0}
                        >
                            <FaShoppingCart className="me-1" /> В корзину
                        </Button>
                    </div>
                </Card.Body>
            </div>
            {renderModal()}
            {/* --- Мобильная адаптивность --- */}
            <style>{`
            @media (max-width: 600px) {
                .product-image {
                    object-fit: contain !important;
                }
                .add-to-cart-button {
                    font-size: 14px !important;
                    min-height: 36px !important;
                    padding: 6px 0 !important;
                }
                .product-card-footer {
                    min-height: 48px !important;
                    padding: 0 !important;
                }
            }
            @media (min-width: 601px) {
                .product-image {
                    object-fit: cover !important;
                }
            }
            `}</style>
        </>
    );
});

export default ProductItem;